<div class="space-y-3">
  <!-- Toolbar -->
  <div class="flex items-center justify-between gap-3">
    <span class="text-xs font-medium text-gray-400">{{ phaseModels.length }} template{{ phaseModels.length !== 1 ? 's' : '' }}</span>
    <button (click)="showAddModal = true"
      class="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm cursor-pointer">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
      Add Template
    </button>
  </div>

  @if (isLoading) {
    <div class="flex flex-col items-center justify-center py-12">
      <div class="w-8 h-8 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3"></div>
      <span class="text-sm text-gray-500">Loading templates...</span>
    </div>
  } @else if (phaseModels.length === 0) {
    <!-- Improved Empty State -->
    <div class="py-12 text-center">
      <div class="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
        <svg class="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
        </svg>
      </div>
      <h3 class="text-sm font-semibold text-gray-900 mb-1">No phase templates yet</h3>
      <p class="text-xs text-gray-500 mb-4 max-w-[200px] mx-auto">
        Create reusable phase templates to quickly set up new projects
      </p>
      <p class="text-xs text-gray-400">
        Click "Add" above to create your first template
      </p>
    </div>
  } @else {
    <div class="space-y-3">
      @for (model of phaseModels; track model.id) {
        <div class="bg-white border border-gray-200 rounded-xl overflow-hidden
                    hover:border-blue-200 hover:shadow-sm transition-all">
          <!-- Model Header -->
          <div class="px-4 py-3 flex justify-between items-center cursor-pointer bg-gray-50/50 hover:bg-gray-50"
               (click)="model.id && toggleExpand(model.id)">
            <div class="flex items-center gap-2 min-w-0 flex-1">
              <div class="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></div>
              <input type="text" [(ngModel)]="model.modelName" (click)="$event.stopPropagation()"
                     class="text-sm font-semibold text-gray-800 bg-transparent border-b border-transparent
                            hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-0.5
                            transition-colors truncate flex-1 min-w-0"/>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <span class="text-xs text-gray-400">{{ model.phases?.length || 0 }} phases</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform"
                   [class.rotate-180]="model.id && isExpanded(model.id)"
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </div>
          </div>

          <!-- Expanded Content -->
          @if (model.id && isExpanded(model.id)) {
            <div class="px-4 py-3 border-t border-gray-100 space-y-2 bg-white">
              <div
                cdkDropList
                (cdkDropListDropped)="onPhaseDrop($event, model)"
                class="space-y-2">
                @for (phase of model.phases; track $index) {
                  <div cdkDrag class="flex flex-col gap-2 group bg-gray-50 p-2 rounded-lg">
                    <div class="flex items-center gap-2">
                      <!-- Drag Handle -->
                      <div cdkDragHandle class="drag-handle cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/>
                        </svg>
                      </div>

                      <input type="color" [(ngModel)]="phase.color"
                             class="w-7 h-7 border border-gray-200 rounded-lg cursor-pointer p-0.5 bg-white flex-shrink-0"/>
                      <input type="text" [(ngModel)]="phase.name" placeholder="Phase Name"
                             class="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-sm
                                    focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 outline-none transition-all"/>
                      <button type="button" (click)="removePhase(model, $index)"
                              class="w-7 h-7 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-500
                                     transition-all cursor-pointer flex items-center justify-center
                                     opacity-0 group-hover:opacity-100">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                    <div class="flex items-center gap-2 pl-7">
                      <label class="text-xs font-semibold text-gray-500">Est. Hours:</label>
                      <input type="number" [(ngModel)]="phase.estimatedHours" min="0" step="0.5" placeholder="e.g. 10"
                             class="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-blue-500/20 outline-none"/>
                    </div>
                  </div>
                }
              </div>

              @if(model.phases?.length === 0) {
                <div class="text-center py-4 text-xs text-gray-400">
                  No phases yet. Click "Add Phase" to create one.
                </div>
              }

              <!-- Actions -->
              <div class="flex justify-between items-center pt-3 mt-2 border-t border-gray-100">
                <button type="button" (click)="openDeleteConfirm(model)"
                        class="text-xs text-red-500 hover:text-red-600 cursor-pointer">
                  Delete template
                </button>
                <div class="flex gap-2">
                  <button type="button" (click)="addPhase(model)"
                          class="px-3 py-1.5 rounded-lg text-xs font-medium
                                 text-blue-600 hover:bg-blue-50
                                 transition-all cursor-pointer">
                    + Add Phase
                  </button>
                  <button type="button" (click)="saveModel(model)"
                          class="px-3 py-1.5 rounded-lg text-xs font-medium text-white
                                 bg-blue-600 hover:bg-blue-700
                                 transition-all cursor-pointer">
                    Save
                  </button>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>
  }
</div>

@if(showConfirmDelete) {
  <app-confirm-modal
    [message]="confirmMessage"
    (close)="handleConfirmDelete($event)">
  </app-confirm-modal>
}

@if(showAddModal) {
  <div class="fixed inset-0 z-9999 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
       (click)="closeAddModal()">
    <div (click)="$event.stopPropagation()" class="w-full max-w-lg">
      <app-add-phase-model (close)="closeAddModal()" />
    </div>
  </div>
}
